import SecurityIcon from '@mui/icons-material/Security';
import {
  Box,
  fontSizes,
  GlassAlert,
  GlassButton,
  GlassTextField,
  Link,
  MagicInput,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import {
  type AuthCredential,
  getMultiFactorResolver,
  type MultiFactorError,
  type MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  signInWithCustomToken,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { auth } from '../config/firebase';
import { useRedirect } from '../hooks/useRedirect';
import { linkPendingCredential } from '../utils/account-linking';

export default function MfaVerifyPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // useRedirect handles the full allowlist gate (platform domains +
  // pre-verified custom domains) and falls back to the portal default
  // when the supplied redirect is rejected. Replaces the previous
  // bespoke `safeRedirectUrlForToken` helper which forgot to thread
  // `verifiedCustomDomains` into isValidRedirectUrl, dropping
  // white-label tenants on custom domains. Refs: gap-finder F-G.
  const { redirect } = useRedirect();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');

  // Get MFA resolver from location state (passed from login page)
  useEffect(() => {
    const state = location.state as { mfaError?: MultiFactorError } | null;
    if (state?.mfaError) {
      try {
        const mfaResolver = getMultiFactorResolver(auth, state.mfaError);
        setResolver(mfaResolver);
      } catch (err) {
        console.error('Failed to get MFA resolver:', err);
        setError(t('auth.mfa.verify.notInitialized'));
      }
    } else {
      setError(t('auth.mfa.verify.notInitializedFirst'));
    }
  }, [location]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take last digit
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      const digit = pastedData[i];
      if (digit !== undefined) {
        newCode[i] = digit;
      }
    }
    setCode(newCode);
    inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
  };

  const handleBackupCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (backupCode.length !== 8) {
      setError(t('auth.mfa.verify.backupCodeLength'));
      return;
    }

    const state = location.state as { email?: string } | null;
    if (!state?.email) {
      setError(t('auth.mfa.verify.emailNotFound'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call public API to verify backup code and get custom token
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/mfa/verify-backup-code-during-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: state.email,
          backupCode: backupCode.toUpperCase().replace(/\s/g, ''),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: t('auth.mfa.verify.invalidBackupCode') }));
        throw new Error(errorData.message || t('auth.mfa.verify.invalidBackupCode'));
      }

      const { customToken } = await response.json();

      // Sign in with custom token
      const userCredential = await signInWithCustomToken(auth, customToken);

      // Get ID token and exchange for our JWT
      const idToken = await userCredential.user.getIdToken();
      const exchangeResponse = await fetch(`${import.meta.env.VITE_API_URL}/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseIdToken: idToken }),
      });

      if (!exchangeResponse.ok) {
        throw new Error('Failed to exchange token');
      }

      const { accessToken } = await exchangeResponse.json();

      // Redirect after successful MFA — gate through isValidRedirectUrl
      // so an attacker can't supply ?redirect=https://evil.com to harvest
      // the post-MFA JWT.
      redirect(accessToken);
    } catch (err) {
      console.error('Backup code verification error:', err);
      const errorMessage = err instanceof Error ? err.message : t('auth.mfa.verify.verifyError');
      setError(
        errorMessage.includes('Invalid') ? t('auth.mfa.verify.invalidBackupCode') : t('auth.mfa.verify.verifyError'),
      );
      setBackupCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const verificationCode = code.join('');

    if (verificationCode.length !== 6) {
      setError(t('auth.mfa.verify.enterAllDigits'));
      return;
    }

    if (!resolver) {
      setError(t('auth.mfa.verify.notInitialized'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get the selected MFA hint (first available factor)
      const selectedHint = resolver.hints[0];
      if (!selectedHint) {
        throw new Error('No MFA factors available');
      }

      // Create assertion based on factor type
      let assertion;
      if (selectedHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        // TOTP (Authenticator app)
        assertion = TotpMultiFactorGenerator.assertionForSignIn(selectedHint.uid, verificationCode);
      } else if (selectedHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        // Phone SMS (not commonly used with TOTP)
        const phoneAuthCredential = PhoneAuthProvider.credential(selectedHint.uid, verificationCode);
        assertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
      } else {
        throw new Error(`Unsupported MFA factor type: ${selectedHint.factorId}`);
      }

      // Resolve the MFA challenge
      const userCredential = await resolver.resolveSignIn(assertion);

      // If Google sign-in started the flow on an existing password account,
      // we captured the Google credential on LoginPage and forwarded it
      // through location.state. Attach it now so the user can use either
      // method next time. Soft-fail — redirect either way.
      const pendingLink = (location.state as { pendingLinkCredential?: AuthCredential } | null)?.pendingLinkCredential;
      if (pendingLink) await linkPendingCredential(userCredential.user, pendingLink);

      // Get ID token and exchange for our JWT
      const idToken = await userCredential.user.getIdToken();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseIdToken: idToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange token');
      }

      const { accessToken } = await response.json();

      // Redirect after successful MFA — gate through isValidRedirectUrl
      // so an attacker can't supply ?redirect=https://evil.com to harvest
      // the post-MFA JWT.
      redirect(accessToken);
    } catch (err) {
      console.error('MFA verification error:', err);
      const errorMessage = err instanceof Error ? err.message : t('auth.mfa.verify.verifyError');
      if (
        errorMessage.includes('invalid-verification-code') ||
        errorMessage.includes('auth/invalid-verification-code')
      ) {
        setError(t('auth.mfa.verify.invalidCode'));
      } else {
        setError(t('auth.mfa.verify.verifyError'));
      }
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  // Get MFA method display name
  const mfaMethod = resolver?.hints[0]?.factorId === TotpMultiFactorGenerator.FACTOR_ID ? 'authenticator app' : 'phone';

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          {t('auth.mfa.verify.title')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {resolver ? t('auth.mfa.verify.subtitle', { method: mfaMethod }) : t('auth.mfa.verify.initializing')}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 3 }}>
        {error}
      </GlassAlert>

      {resolver ? (
        useBackupCode ? (
          <form onSubmit={handleBackupCodeSubmit}>
            <MagicInput
              label={t('auth.mfa.verify.backupCodeLabel')}
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="ABCD-EFGH"
              slotProps={{
                htmlInput: {
                  maxLength: 9,
                  style: {
                    textAlign: 'center',
                    fontSize: fontSizes.xl,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                  },
                },
              }}
              sx={{ mb: 3 }}
              autoFocus
            />

            <GlassButton
              type="submit"
              variant="subtle"
              fullWidth
              size="large"
              disabled={isLoading || backupCode.length !== 8}
              sx={{ mb: 2 }}
            >
              {isLoading ? (
                <ShimmerLoader variant="circular" width={24} height={24} />
              ) : (
                t('auth.mfa.verify.verifyBackupCode')
              )}
            </GlassButton>

            <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
              <Link
                component="button"
                type="button"
                onClick={() => {
                  setUseBackupCode(false);
                  setBackupCode('');
                  setError(null);
                }}
              >
                {t('auth.mfa.verify.useAuthenticator')}
              </Link>
            </Typography>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {code.map((digit, index) => (
                <GlassTextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  slotProps={{
                    htmlInput: {
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: fontSizes['2xl'],
                        fontWeight: 600,
                        padding: '12px 0',
                      },
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    },
                  }}
                  sx={{ width: 48 }}
                />
              ))}
            </Box>

            <GlassButton
              type="submit"
              variant="subtle"
              fullWidth
              size="large"
              disabled={isLoading || code.join('').length !== 6}
              sx={{ mb: 2 }}
            >
              {isLoading ? (
                <ShimmerLoader variant="circular" width={24} height={24} />
              ) : (
                t('auth.mfa.verify.verifyButton')
              )}
            </GlassButton>

            <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
              <Link
                component="button"
                type="button"
                onClick={() => {
                  setUseBackupCode(true);
                  setCode(['', '', '', '', '', '']);
                  setError(null);
                }}
              >
                {t('auth.mfa.verify.useBackupCode')}
              </Link>
            </Typography>
          </form>
        )
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, display: 'flex', justifyContent: 'center' }}>
          <ShimmerLoader variant="rectangular" width={200} height={60} />
        </Box>
      )}

      <Box sx={{ textAlign: 'center' }}>
        {mfaMethod === 'authenticator app' && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('auth.mfa.verify.codeChanges')}
          </Typography>
        )}

        <Typography variant="body2" color="textSecondary">
          <Link component={RouterLink} to={`/login${queryString}`}>
            {t('auth.mfa.verify.useDifferentAccount')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
